import {Component, Inject, ViewChild, AfterViewInit} from '@angular/core';
import {MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog';
import {MatTableDataSource} from '@angular/material/table';
import {MatSort} from '@angular/material/sort';
import {MatPaginator} from '@angular/material/paginator';
import {RagService} from '../../services/ai/rag.service';
import {MatSnackBar} from '@angular/material/snack-bar';

export interface TutorialItem {
    name: string;
    path: string;
    videoId: string;
}

@Component({
    selector: 'app-gpt-response-dialog',
    templateUrl: './gpt-response-dialog.component.html',
    styleUrls: ['./gpt-response-dialog.component.scss'],
    standalone: false
})
export class GptResponseDialogComponent implements AfterViewInit {
    
    @ViewChild(MatSort) sort: MatSort;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    
    displayedColumns: string[] = ['select', 'videoId', 'path'];
    dataSource: MatTableDataSource<TutorialItem>;
    selection: Set<TutorialItem> = new Set();
    
    constructor(
        public dialogRef: MatDialogRef<GptResponseDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { 
            tutorials: TutorialItem[], 
            message: string,
            count: number 
        },
        private ragService: RagService,
        private snackBar: MatSnackBar
    ) {
        this.dataSource = new MatTableDataSource(data.tutorials || []);
    }

    ngAfterViewInit() {
        if (this.dataSource) {
            this.dataSource.sort = this.sort;
            // Removed paginator to show all items in scroll
            // this.dataSource.paginator = this.paginator;
        }
    }

    onClose(): void {
        this.dialogRef.close();
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    onOk(): void {
        // Get all selected items
        const selectedItems = Array.from(this.selection);
        
        if (selectedItems.length > 0) {
            console.log('=== Selected Tutorials ===');
            console.log(`Total selected: ${selectedItems.length}`);
            console.log('Selected items:');
            
            selectedItems.forEach((item, index) => {
                console.log(`${index + 1}. Video ID: ${item.videoId}, Path: ${item.path}`);
            });
            
            // Also log as structured data for easy copying
            console.log('Selected items as JSON:');
            console.log(JSON.stringify(selectedItems, null, 2));
            
            // Extract tutorials with video IDs and subjects (paths) from selected items
            const tutorialsToUpdate = selectedItems
                .filter(item => item.videoId && item.videoId.trim() !== '')
                .map(item => ({
                    videoId: item.videoId,
                    subject: item.path // Use the path as the subject/question
                }));
            
            if (tutorialsToUpdate.length > 0) {
                console.log('Calling UpdateTutorials API with tutorials:', tutorialsToUpdate);
                
                // Call the RAG API with selected tutorials
                this.ragService.updateTutorials(tutorialsToUpdate).subscribe({
                    next: (response) => {
                        console.log('UpdateTutorials API response:', response);
                        
                        // Show detailed response information
                        if (response.errors && response.errors.length > 0) {
                            console.error('Errors during processing:', response.errors);
                            this.snackBar.open(`Processed ${response.processedCount} of ${response.totalCount} tutorials. Check console for errors.`, 'Close', {
                                duration: 5000
                            });
                        } else {
                            this.snackBar.open(`Successfully processed ${response.processedCount} of ${response.totalCount} tutorials`, 'Close', {
                                duration: 3000
                            });
                        }
                        
                        // Show any errors in the console
                        if (response.errors && response.errors.length > 0) {
                            console.warn('Tutorial processing errors:');
                            response.errors.forEach((error: string) => console.warn(` - ${error}`));
                        }
                        
                        // Close dialog and return selected items
                        this.dialogRef.close(selectedItems);
                    },
                    error: (error) => {
                        console.error('Error calling UpdateTutorials API:', error);
                        console.error('Error response:', error.error);
                        this.snackBar.open(`Error: ${error.error?.message || error.message || 'Failed to process tutorials'}`, 'Close', {
                            duration: 5000
                        });
                        // Still close dialog even if API fails
                        this.dialogRef.close(selectedItems);
                    }
                });
            } else {
                console.log('No valid video IDs in selected items');
                this.snackBar.open('No valid video IDs in selected items', 'Close', {
                    duration: 3000
                });
                this.dialogRef.close(selectedItems);
            }
        } else {
            console.log('No items selected');
            this.snackBar.open('Please select at least one tutorial', 'Close', {
                duration: 3000
            });
        }
    }

    isAllSelected(): boolean {
        const numSelected = this.selection.size;
        const numRows = this.dataSource.data.length;
        return numSelected === numRows;
    }

    isSomeSelected(): boolean {
        return this.selection.size > 0 && !this.isAllSelected();
    }

    masterToggle(): void {
        if (this.isAllSelected()) {
            this.selection.clear();
        } else {
            this.dataSource.data.forEach(row => this.selection.add(row));
        }
    }

    toggleRow(row: TutorialItem): void {
        if (this.selection.has(row)) {
            this.selection.delete(row);
        } else {
            this.selection.add(row);
        }
    }

    isSelected(row: TutorialItem): boolean {
        return this.selection.has(row);
    }

    copyToClipboard(): void {
        // Copy selected items if any, otherwise copy all
        const itemsToCopy = this.selection.size > 0 
            ? Array.from(this.selection)
            : this.data.tutorials;
            
        const jsonString = JSON.stringify(itemsToCopy, null, 2);
        navigator.clipboard.writeText(jsonString).then(() => {
            console.log(`Copied ${itemsToCopy.length} items to clipboard`);
        }).catch(err => {
            console.error('Failed to copy text: ', err);
        });
    }

    exportAsCSV(): void {
        // Export selected items if any, otherwise export all
        const itemsToExport = this.selection.size > 0 
            ? Array.from(this.selection)
            : this.data.tutorials;
            
        if (!itemsToExport || itemsToExport.length === 0) return;
        
        const csvContent = [
            ['Name', 'Path', 'Video ID'],
            ...itemsToExport.map(item => [
                item.name || '',
                item.path || '',
                item.videoId || ''
            ])
        ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `tutorials_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    }
}